import { useEffect, useState } from "react";
import { api } from "./api";
import { AlertTriangle, OctagonAlert, X } from "lucide-react";

const STYLES = {
  error:   { bar: "bg-rose-600 text-white", Icon: OctagonAlert },
  warning: { bar: "bg-amber-500 text-white", Icon: AlertTriangle },
};

export default function AlertBanner({ onOpenTab }) {
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    const h = (e) => setAlerts((prev) => [e.detail, ...prev.filter((a) => a.id !== e.detail.id)].slice(0, 15));
    window.addEventListener("kairos:alert", h);
    return () => window.removeEventListener("kairos:alert", h);
  }, []);

  const dismissAll = () => {
    alerts.forEach((a) => api.post(`/notifications/${a.id}/read`).catch(() => {}));
    setAlerts([]);
  };
  const openAlert = (a) => {
    const m = (a.link || "").match(/tab=(\w+)/);
    if (m && onOpenTab) onOpenTab(m[1]);
    setAlerts((prev) => prev.filter((x) => x.id !== a.id));
    api.post(`/notifications/${a.id}/read`).catch(() => {});
  };

  if (!alerts.length) return null;
  const { bar, Icon } = STYLES[alerts.some((a) => a.severity === "error") ? "error" : "warning"] || STYLES.warning;
  const dur = Math.max(18, alerts.length * 14);
  const sorted = [...alerts].sort((a, b) => (a.severity === "error" ? 0 : 1) - (b.severity === "error" ? 0 : 1));

  const items = sorted.map((a) => (
    <button
      key={a.id}
      data-testid={`alert-ticker-item-${a.id}`}
      onClick={() => openAlert(a)}
      title="Open — this alert will be marked read"
      className="inline-flex items-center gap-2 whitespace-nowrap px-6 hover:underline underline-offset-2"
    >
      <Icon size={14} className="shrink-0 opacity-90" />
      <b className="font-bold">{a.title || "Alert"}</b>
      {a.body ? <span className="opacity-90"> — {a.body}</span> : null}
    </button>
  ));

  return (
    <div data-testid="alert-banner" className={`${bar} k-ticker relative z-30 w-full flex items-center text-sm shadow-sm`}>
      <span data-testid="alert-ticker-count"
            className="shrink-0 ml-3 my-1.5 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wide bg-white/20 rounded-full px-2.5 py-1">
        <AlertTriangle size={12} /> {alerts.length} alert{alerts.length > 1 ? "s" : ""}
      </span>
      <div className="flex-1 min-w-0 overflow-hidden py-2" data-testid="alert-ticker">
        <div className="k-ticker-track flex w-max" style={{ "--k-ticker-dur": `${dur}s` }}>
          <div className="flex w-max shrink-0">{items}</div>
          <div className="flex w-max shrink-0" aria-hidden="true">{items}</div>
        </div>
      </div>
      <button
        data-testid="alert-banner-dismiss-all"
        onClick={dismissAll}
        title={`Dismiss all ${alerts.length} alert${alerts.length > 1 ? "s" : ""}`}
        className="shrink-0 mr-3 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-bold hover:bg-white/20 transition-colors"
      >
        <X size={14} /> Dismiss all
      </button>
    </div>
  );
}
